// Pure geometry helpers for look-editor text fields.
//
// Background (#4575): the production renderer (EC2 pose_renderer.py) anchors
// text at the authored center_x and NEVER clamps to the bounds box — a
// center_x left orphaned outside [bound_x_left, bound_x_right] draws text
// mid-canvas or off the edge. The editor must (a) stop creating that state
// (clampCenterToBounds, used on horizontal resize) and (b) stop hiding it
// (textFieldExtent, used to draw the overflow warning instead of clipping).

/**
 * Clamp a text field's center_x so it stays inside its horizontal bounds.
 *
 * Chosen behavior: CLAMP, not proportional re-centering — the center stays
 * exactly where the designer put it until a dragged edge passes it, then it
 * rides along that edge. Proportional re-centering would silently move text
 * that the designer never touched, which is more surprising.
 *
 * All three values are 0-1 canvas fractions. Null/undefined center passes
 * through untouched (nothing to clamp).
 */
export function clampCenterToBounds(centerX, boundLeft, boundRight) {
  if (centerX == null) return centerX;
  const left = boundLeft ?? 0;
  const right = boundRight ?? 1;
  return Math.min(Math.max(centerX, left), right);
}

/**
 * Compute the horizontal extent a text field's content actually occupies when
 * drawn, and whether it escapes its bounds box — the same anchor math the
 * production renderer uses (center-anchor at centerX; left/right align anchor
 * at the bound with a 2px inset).
 *
 * All inputs are in the SAME pixel space (preview px or canvas px):
 *   align      'left' | 'right' | 'center' (default center)
 *   centerX    anchor x for center align
 *   left/right the bounds box edges
 *   textWidth  measured width of the rendered text
 *
 * Returns { textLeft, textRight, overflowsBounds } — overflowsBounds is true
 * when any part of the drawn text lies outside [left, right] (beyond a small
 * tolerance for the align inset / antialiasing).
 */
export function textFieldExtent({
  align, centerX, left, right, textWidth, tolerance = 3,
}) {
  let textLeft;
  if (align === 'left') {
    textLeft = left + 2;
  } else if (align === 'right') {
    textLeft = right - 2 - textWidth;
  } else {
    textLeft = centerX - textWidth / 2;
  }
  const textRight = textLeft + textWidth;
  const overflowsBounds = textLeft < left - tolerance || textRight > right + tolerance;
  return { textLeft, textRight, overflowsBounds };
}
